import { FC, useEffect, useState } from 'react';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { DataView } from 'primereact/dataview';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { getProgress, saveProgress } from '../../businessLogicService/practiceProblemsHelper';
import type { LearnosityErrorShape } from '../../utils/learnositySentry';
import {
  useLearnosityItemsInitialization,
  type LearnosityInitializationStatus,
  type LearnosityInitializationRefetch,
} from '../../custom-hooks/useLearnosityItemsInitialization';
import { usePreviewMode } from '../../contexts/PreviewModeContext';

interface Props {
  itemRefs: string[];
  initObj: LearnosityRequest;
  setPrintDisabled?: (state: boolean) => void;
  setShowBadge: (index: boolean) => void;
  setShowRetryButton: (index: boolean) => void;
  skillCode: string;
  onInitError?: () => void;
  onStatusChange?: (status: LearnosityInitializationStatus) => void;
  refetchInitialization: LearnosityInitializationRefetch;
}

const HEADER_OFFSET = 105;

export const InlineAssessmentWrapper: FC<Props> = ({
  initObj,
  itemRefs,
  setPrintDisabled,
  setShowBadge,
  setShowRetryButton,
  skillCode,
  onInitError,
  onStatusChange,
  refetchInitialization,
}) => {
  const userRedux = useAppSelector(selectUser);
  const previewMode = usePreviewMode();
  const [itemContainerKey, setItemContainerKey] = useState(0);
  const { status, itemsAppRef } = useLearnosityItemsInitialization({
    initObj,
    enabled: itemRefs.length > 0,
    restartKey: previewMode ? 'preview' : 'assessment',
    assessmentType: 'inline_assessment',
    refetchInitialization,
    getContainer: () => document.getElementById('print-practice-problems'),
    onFailure: error => {
      // Startup exhaustion clears the ref; only a ready player's math error
      // requests a same-session signing restart from the parent.
      if (itemsAppRef.current && String((error as LearnosityErrorShape | null)?.code) === '10023') onInitError?.();
    },
    onReady: itemsApp => {
      const items = itemsApp?.getItems();
      const responses = itemsApp?.getResponses();

      // If there are responses in Learnosity, it means that the user has already started the practice problems, so we show the retry button
      if (Object.keys(responses || {}).length !== 0 && items) {
        setShowRetryButton(true);
      } else {
        setShowRetryButton(false);
      }

      // Scroll to the item that the user was on before, if there is one
      const reference = getProgress(userRedux?.id ?? '', skillCode);
      if (reference) {
        const el = document.getElementById(reference);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
          window.scrollTo({
            top: y,
            behavior: 'auto',
          });
        }
      }

      const responseIdToReferenceMap: Record<string, string> = {};
      if (items) {
        Object.values(items).forEach(el => {
          el.response_ids?.forEach(responseId => {
            if (responseId) responseIdToReferenceMap[responseId] = el.reference;
          });
        });
        Object.entries(responseIdToReferenceMap).forEach(([responseId, reference]) => {
          itemsApp?.question(responseId).on('changed', function () {
            if (reference) saveProgress(userRedux?.id ?? '', skillCode, reference);
            itemsApp?.save();
            setShowBadge(true);
            setShowRetryButton(true);
          });
        });
      }
    },
  });

  useEffect(() => {
    // Cached Questions initialization can fail after replacing the placeholders,
    // before Items installs a working reset. Rebuild only its DOM for the retry.
    if (status === 'retry_wait') setItemContainerKey(key => key + 1);
  }, [status]);

  useEffect(() => {
    setPrintDisabled?.(status !== 'ready');
    onStatusChange?.(status);
  }, [status, setPrintDisabled, onStatusChange]);

  let itemNumber = 1;

  const practiceTemplate = (reference: string) => {
    return (
      <div className={`"flex w-full nc_practice-problem " ${itemNumber % 2 === 0 ? 'break-page' : ''}`} id={reference}>
        <h1 id="practice-index">{itemNumber++}</h1>
        <span key={reference} className="learnosity-item" data-reference={reference}></span>
      </div>
    );
  };

  return (
    <div className="nc_inline-assessment align-self-center" id="print-practice-problems">
      <DataView key={itemContainerKey} value={itemRefs} itemTemplate={practiceTemplate} />
    </div>
  );
};
